import { AuthFormView } from "@secure-vault/ui";
import { useRef, useState } from "react";

import { useAuth } from "@/lib/auth-context";

/**
 * Vault sign-in screen. Shares its look with apps/app's advisor sign-in page via
 * the AuthFormView component in @secure-vault/ui — same card, same Aura tokens.
 * The vault population only has the magic-link flow (no passkeys, no
 * self-service registration — accounts are provisioned server-side), so this is
 * a thin wrapper: local form state + useAuth().requestMagicLink.
 */
export function LoginRoute() {
  const { requestMagicLink } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSubmitting = useRef(false);

  const submit = async () => {
    if (loading || isSubmitting.current || !email.trim()) return;
    isSubmitting.current = true;
    setLoading(true);
    setError(null);
    try {
      await requestMagicLink(email.trim());
      setSent(true);
    } catch (err) {
      console.error("Failed to send vault sign-in link:", err);
      setError(
        "We couldn't send your sign-in link. Check your email address and try again."
      );
    } finally {
      setLoading(false);
      isSubmitting.current = false;
    }
  };

  const handleChangeEmail = (v: string) => {
    setEmail(v);
    if (sent) setSent(false);
  };

  return (
    <AuthFormView
      mode="signin"
      audience="client"
      email={email}
      sent={sent}
      loading={loading}
      error={error}
      onChangeEmail={handleChangeEmail}
      onSubmit={submit}
      onResend={() => {
        setSent(false);
        void submit();
      }}
      onCancelSent={() => {
        setSent(false);
      }}
      onSwitchMode={() => {
        /* no-op: the vault app has no self-service registration */
      }}
      signInTitle="Access your vault"
      signInSubtitle="Enter your email and we'll send you a secure sign-in link. After signing in, enter your vault password to open your documents."
    />
  );
}
